import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Building, Copy } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDate } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function MyAgency() {
  const { agency, setAgency } = useAuth();
  const [form, setForm] = useState({
    name: agency?.name || '',
    currency_symbol: agency?.currency_symbol || '€',
    debrief_email: agency?.debrief_email || 'weekly',
    paypal_email: agency?.paypal_email || '',
  });
  const [saving, setSaving] = useState(false);
  const [pp, setPp] = useState(null);  // { secret, url } pour brancher le script PayPal

  useEffect(() => {
    api.get('/agency/paypal-config').then(({ data }) => setPp(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/agency', form);
      setAgency(data);
      toast.success('Paramètres enregistrés');
    } catch (e) { toast.error(apiError(e, "Échec de l'enregistrement")); } finally { setSaving(false); }
  };

  const scriptSnippet = `const CHATHUB_URL = '${(pp && pp.url) || 'https://api.chat-hubs.com/api/paypal/notify'}';
const CHATHUB_SECRET = '${(pp && pp.secret) || 'TON_SECRET'}';

// Tourne tout seul (déclencheur temporel). Lit tes mails PayPal et envoie chaque paiement
// REÇU à ChatHub. Anti-doublon PAR MESSAGE (aucune notif en double, même toutes les minutes).
// Aucun mot de passe, aucun accès à ton compte côté ChatHub.
function checkPayPal(){
  var props = PropertiesService.getScriptProperties();
  var seen = JSON.parse(props.getProperty('chathub_seen') || '{}');
  var changed = false;
  GmailApp.search('from:paypal newer_than:2d', 0, 30).forEach(function(t){
    t.getMessages().forEach(function(m){
      var id = m.getId();
      if (seen[id]) return;            // déjà envoyé → on saute
      var info = parsePayPal(m);
      if (info) postToChatHub(info);
      seen[id] = 1; changed = true;    // marqué traité (même si pas un paiement)
    });
  });
  if (changed){ var ids = Object.keys(seen); var keep = ids.length > 300 ? ids.slice(-300) : ids;
    var o = {}; keep.forEach(function(k){ o[k] = 1; }); props.setProperty('chathub_seen', JSON.stringify(o)); }
}

// Lance UNE fois à la main pour rapatrier l'historique (1 an). Sans doublon.
function importHistory(){
  GmailApp.search('from:paypal newer_than:1y', 0, 300).forEach(function(t){
    t.getMessages().forEach(function(m){ var info = parsePayPal(m); if (info) postToChatHub(info); });
  });
}

// Parseur éprouvé : ne garde que les paiements REÇUS (ignore reçus de carte, retraits,
// connexions, demandes d'argent, paiements sortants…).
function parsePayPal(m){
  var b = m.getPlainBody() || '';
  var recu = /(has sent you|vous a envoyé|you received|vous avez reçu)/i.test(b);
  var sortant = /(you sent|vous avez envoyé|withdraw|retrait|new device|login|connexion|password|mot de passe|money request|demande d.argent|your receipt|you paid|vous avez payé)/i.test(b);
  if (!recu || sortant) return null;
  var mAmt = b.match(/€\\s?([0-9][0-9\\s.,]*)\\s?EUR/i) || b.match(/([0-9][0-9\\s.,]*)\\s?EUR/i) || b.match(/EUR\\s?([0-9][0-9.,]*)/i);
  if (!mAmt) return null;
  var raw = mAmt[1].replace(/\\s/g, '');
  var amount = raw.indexOf(',') >= 0 ? parseFloat(raw.replace(/\\./g, '').replace(',', '.')) : parseFloat(raw);
  if (!amount) return null;
  var mP = b.match(/(?:^|\\n)\\s*([A-Za-zÀ-ÿ][^\\n]{1,50}?)\\s+(?:has sent you|vous a envoyé)/);
  var payer = mP ? mP[1].trim() : '';
  var mail = '';
  (b.match(/[\\w.+-]+@[\\w.-]+\\.[a-z]{2,}/ig) || []).some(function(e){ if (!/paypal\\./i.test(e)) { mail = e; return true; } return false; });
  return { amount: amount, payer: payer, payer_email: mail, txid: m.getId(), date: m.getDate().toISOString() };
}

function postToChatHub(info){
  try { UrlFetchApp.fetch(CHATHUB_URL, { method:'post', contentType:'application/json',
    payload: JSON.stringify({ secret: CHATHUB_SECRET, amount: info.amount, currency:'EUR', payer: info.payer||'', payer_email: info.payer_email||'', txid: info.txid||'', date: info.date||'' }),
    muteHttpExceptions:true }); } catch(e){}
}`;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto fade-in" data-testid="my-agency-page">
      <PageHeader title="Mon agence" subtitle="Paramètres de l'organisation" />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-5">
        <div className="flex items-center gap-4 pb-5 border-b border-white/5">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center"><Building size={24} className="text-white" /></div>
          <div>
            <div className="text-lg font-bold text-white" data-testid="agency-name-display">{agency?.name}</div>
            <div className="text-sm text-slate-400">Créée le {fmtDate(agency?.created_at)}</div>
          </div>
          <Badge className="ml-auto bg-indigo-500/15 text-indigo-300 border-indigo-500/30">Offre {agency?.plan}</Badge>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Nom de l'agence</Label>
            <Input data-testid="agency-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Devise</Label>
            <select data-testid="agency-currency-select" value={form.currency_symbol} onChange={e => setForm({ ...form, currency_symbol: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
              <option value="€" className="bg-[#0B0B0E]">€ Euro</option>
              <option value="$" className="bg-[#0B0B0E]">$ Dollar US</option>
              <option value="£" className="bg-[#0B0B0E]">£ Livre sterling</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">E-mail de débrief</Label>
            <select data-testid="agency-debrief-select" value={form.debrief_email} onChange={e => setForm({ ...form, debrief_email: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
              <option value="disabled" className="bg-[#0B0B0E]">Désactivé</option>
              <option value="daily" className="bg-[#0B0B0E]">Quotidien</option>
              <option value="weekly" className="bg-[#0B0B0E]">Hebdomadaire</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Code de parrainage</Label>
            <div className="flex gap-2">
              <Input value={agency?.referral_code || ''} disabled className="bg-white/5 border-white/10 text-slate-400 h-10 font-mono" />
              <Button variant="outline" size="icon" className="border-white/10 bg-white/5 text-slate-300 h-10 w-10" onClick={() => { navigator.clipboard?.writeText(agency?.referral_code || ''); toast.success('Copié'); }}><Copy size={14} /></Button>
            </div>
          </div>
        </div>

        <Button data-testid="agency-save-button" className="bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-4 mt-6" data-testid="paypal-settings">
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-white">Paiements PayPal</span>
          <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30">Optionnel</Badge>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">
          Pour que tes ventes et dons <span className="text-white">PayPal</span> apparaissent dans ChatHub (attribués au bon
          fan dès qu'il a payé une fois), on utilise un petit <span className="text-white">Google Apps Script</span> qui tourne
          dans TON compte Google et lit tes mails PayPal. <span className="text-white">Aucun mot de passe, aucun accès à ton compte côté ChatHub.</span>
          {' '}Tu n'as <span className="text-white">rien à renseigner ici</span> : tout passe par le script + ton secret ci-dessous.
        </p>
        {pp && (pp.received > 0
          ? <div className="flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.08] px-4 py-2.5 text-[13px] text-emerald-200">
              <span>✅</span><span><span className="font-semibold">{pp.received} paiement(s) PayPal déjà reçu(s)</span>{pp.last_received ? ` · dernier le ${fmtDate(pp.last_received)}` : ''} — c'est branché et ça tourne.</span>
            </div>
          : <div className="flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-2.5 text-[13px] text-amber-100/90">
              <span>⏳</span><span>Aucun paiement PayPal reçu pour l'instant. Si tu viens d'installer le script, lance <span className="font-mono">importHistory</span> (voir plus bas) pour vérifier qu'il envoie bien vers ChatHub.</span>
            </div>)}
        <div className="space-y-1.5 max-w-sm">
          <Label className="text-slate-300 text-xs">Ton secret (déjà inclus dans le code ci-dessous)</Label>
          <div className="flex gap-2">
            <Input value={(pp && pp.secret) || '…'} disabled className="bg-white/5 border-white/10 text-slate-300 h-10 font-mono text-xs" />
            <Button variant="outline" size="icon" className="border-white/10 bg-white/5 text-slate-300 h-10 w-10" onClick={() => { navigator.clipboard?.writeText((pp && pp.secret) || ''); toast.success('Secret copié'); }}><Copy size={14} /></Button>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/40 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">Le script complet à coller (ton secret est déjà dedans)</span>
            <Button variant="outline" size="sm" className="border-white/10 bg-white/5 text-slate-300 h-7 text-[11px]" onClick={() => { navigator.clipboard?.writeText(scriptSnippet); toast.success('Code copié'); }}><Copy size={12} className="mr-1" /> Copier le code</Button>
          </div>
          <pre className="text-[11px] text-emerald-300/90 font-mono whitespace-pre-wrap overflow-x-auto">{scriptSnippet}</pre>
        </div>
        <ol className="text-xs text-slate-400 list-decimal list-inside space-y-1 pt-1">
          <li>Va sur <span className="text-slate-200">script.google.com</span> → <span className="text-slate-200">Nouveau projet</span>.</li>
          <li>Colle <span className="text-slate-200">tout le code</span> ci-dessus, puis <span className="text-slate-200">Enregistre</span> (icône disquette).</li>
          <li>Clique <span className="text-slate-200">Exécuter</span> une fois (sur <span className="font-mono">checkPayPal</span>) et <span className="text-slate-200">autorise</span> quand Google le demande (le script lit uniquement tes mails PayPal).</li>
          <li>Dans le menu de gauche, icône <span className="text-slate-200">Déclencheurs</span> (la petite horloge ⏰) → bouton <span className="text-slate-200">+ Ajouter un déclencheur</span> en bas à droite → fonction <span className="font-mono">checkPayPal</span>, source de l'événement <span className="text-slate-200">Déclencheur horaire</span>, <span className="text-slate-200">Minuteur (toutes les minutes</span> pour le temps réel, ou 10 min) → <span className="text-slate-200">Enregistrer</span>.</li>
          <li><span className="text-slate-200">C'est tout.</span> À chaque paiement, il arrive dans ChatHub en « à rattacher ». (Rien à enregistrer ici — pas besoin de ton e-mail.)</li>
        </ol>
        <div className="flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-slate-400">
          <span className="text-emerald-400">⏱️</span>
          <span><span className="text-slate-200">Fréquence :</span> « toutes les minutes » = paiement vu quasi en direct (parfait pour que les chatteurs réagissent tout de suite). Le script est <span className="text-slate-200">anti-doublon</span>, donc aucune notif en double même à la minute. (Sur un Gmail gratuit, Google plafonne le temps total à ~90 min/jour ; si un jour le script s'arrête, repasse à 2 min — toujours quasi temps réel.)</span>
        </div>
        <div className="flex items-start gap-2 rounded-xl border border-sky-500/15 bg-sky-500/[0.04] px-3 py-2 text-[11px] text-slate-400">
          <span className="text-sky-400">📥</span>
          <span><span className="text-slate-200">Reprendre l'historique :</span> lance <span className="font-mono text-slate-200">importHistory</span> une seule fois (menu Exécuter) → tous tes paiements PayPal de l'année remontent dans ChatHub en « à rattacher » (sans doublon), prêts à être rattachés à leurs fans. <span className="text-slate-500">Déjà un script qui lit tes mails PayPal ? Garde-le et appelle simplement <span className="font-mono">postToChatHub(info)</span> avec ton paiement.</span></span>
        </div>
      </div>
    </div>
  );
}
